import type { IMarket, IMatch, ISelection } from '@/interface/IMatch';
import type { IOdds } from '@/interface/IOdds';
import type {
    IDynamicMarketTicket,
    IOutrightTicket,
    ISingleTicket,
    ITicket,
    ITicketError,
    Ticket,
    VoucherCapableTicket,
} from '@/interface/ITicket';
import type { ITranslation } from '@/interface/ITranslation';
import {
    type BetPageType,
    PriceChangeStatus,
    TicketType,
} from '@/interface/enum';
import { calculateVoucherMaxPayout, getMaxPayout } from '@/libraries/betSlip/payout';
import { calculateMaxStake } from '@/libraries/betSlip/stake';
import type { IEuroOutrightTicket, IEuroTicket } from '@/libraries/euroTicket';

export function createSingleTicketKey(oddsId: number, option: string): string {
    return `single:${oddsId}:${option}`;
}

export function createDynamicMarketTicketKey(selectionId: number): string {
    return `dynamicMarket:${selectionId}`;
}

export function createOutrightTicketKey(outrightId: number): string {
    return `outright:${outrightId}`;
}

export function createSingleTicket(ticket: IEuroTicket): ISingleTicket {
    return {
        ...createBaseTicket(ticket.betPage),
        key: createSingleTicketKey(ticket.oddsId, ticket.option),
        uid: ticket.uid,
        minBet: ticket.minBet,
        maxBet: ticket.maxBet,
        type: TicketType.Single,
        oddsId: ticket.oddsId,
        option: ticket.option,
        price: ticket.price,
        priceStyle: ticket.priceStyle,
        eventId: ticket.eventId,
        event: copyMatch(ticket.event),
        odds: copyOdds(ticket.odds),
        hasMixParlay: ticket.odds.hasMixParlay,
    };
}

export function createDynamicMarketTicket(
    match: IMatch,
    market: IMarket,
    selection: ISelection,
    betPage: BetPageType,
): IDynamicMarketTicket {
    return {
        ...createBaseTicket(betPage),
        key: createDynamicMarketTicketKey(selection.id),
        type: TicketType.DynamicMarket,
        match: {
            leagueId: match.leagueId,
            leagueSize: match.leagueSize,
            leagueName: copyTranslations(match.leagueName),
            matchId: match.eventId,
            isLive: match.isLive,
            homeTeamName: copyTranslations(match.homeTeam.teamName),
            awayTeamName: copyTranslations(match.awayTeam.teamName),
            sportType: match.sportType,
            liveHomeScore: match.mainMarketEventResult.liveHomeScore,
            liveAwayScore: match.mainMarketEventResult.liveAwayScore,
        },
        market: {
            id: market.id,
            name: market.name,
        },
        selection: {
            id: selection.id,
            name: selection.name,
            price: {
                value: selection.price.value,
                priceStyle: selection.price.priceStyle,
            },
            score: null,
            betCondition: null,
        },
        priceStyle: selection.price.priceStyle,
    };
}

export function createOutrightTicket(ticket: IEuroOutrightTicket): IOutrightTicket {
    return {
        ...createBaseTicket(ticket.betPage),
        key: createOutrightTicketKey(ticket.id),
        uid: ticket.uid,
        minBet: ticket.minBet,
        maxBet: ticket.maxBet,
        type: TicketType.Outright,
        id: ticket.id,
        price: ticket.price,
        priceStyle: ticket.priceStyle,
        leagueId: ticket.leagueId,
        leagueName: copyTranslations(ticket.leagueName),
        teamName: copyTranslations(ticket.teamName),
        sportType: ticket.sportType,
        isLive: ticket.isLive,
    };
}

export function getTicketPrice(ticket: Ticket): number {
    switch (ticket.type) {
        case TicketType.Single:
        case TicketType.Outright:
            return ticket.price;
        case TicketType.DynamicMarket:
            return ticket.selection.price?.value ?? 0;
        default:
            return assertNever(ticket);
    }
}

export function getTicketRequiredBalance(
    ticket: Ticket,
    stake: number = ticket.stake,
): number {
    const price = getTicketPrice(ticket);
    return price < 0 ? Math.abs(price) * stake : stake;
}

export function getTotalTicketRequiredBalance(
    tickets: Ticket[],
): number {
    return tickets.reduce(
        (total, ticket) => total + getTicketRequiredBalance(ticket),
        0,
    );
}

export function getAffordableTicketStake(
    ticket: Ticket,
    balance: number,
): number {
    return Math.min(
        ticket.maxBet,
        calculateMaxStake(balance, getTicketPrice(ticket)),
    );
}

export function isVoucherCapable(ticket: Ticket): ticket is VoucherCapableTicket {
    return ticket.type === TicketType.Single || ticket.type === TicketType.DynamicMarket;
}

export function getTicketMaxPayout(ticket: Ticket, redeemedVoucherStake: number | null = null): number {
    if (redeemedVoucherStake !== null && isVoucherCapable(ticket)) {
        return calculateVoucherMaxPayout(redeemedVoucherStake, getTicketPrice(ticket), ticket.priceStyle);
    }
    return getMaxPayout(ticket.stake, getTicketPrice(ticket), ticket.priceStyle);
}

export function validateTicketStake(ticket: Ticket): ITicketError | null {
    if (ticket.stake === 0) {
        return null;
    }
    if (ticket.stake < ticket.minBet || ticket.stake > ticket.maxBet) {
        return {
            messageKey: 'bet_slip_error_stake_over_under_limit',
            messageData: {
                min: ticket.minBet,
                max: ticket.maxBet,
            },
        };
    }
    return null;
}

export function hasPlaceableStake(ticket: Ticket): boolean {
    return ticket.stake > 0 && validateTicketStake(ticket) === null;
}

export function hasStakeAndUnacceptedOddsChange(ticket: Ticket): boolean {
    return (
        ticket.stake > 0
        && ticket.oddsChangeStatus !== PriceChangeStatus.Remain
        && !ticket.isOddsChangeAccepted
    );
}

export function createPlaceTicketSnapshot(ticket: Ticket): Ticket {
    switch (ticket.type) {
        case TicketType.Single:
            return {
                ...ticket,
                event: {
                    ...ticket.event,
                },
                odds: {
                    ...ticket.odds,
                    eventResult: {
                        ...ticket.odds.eventResult,
                    },
                },
            };
        case TicketType.DynamicMarket:
            return {
                ...ticket,
                match: {
                    ...ticket.match,
                },
                market: {
                    ...ticket.market,
                },
                selection: {
                    ...ticket.selection,
                    price: ticket.selection.price === null
                        ? null
                        : {
                            ...ticket.selection.price,
                        },
                    score: ticket.selection.score === null
                        ? null
                        : {
                            ...ticket.selection.score,
                        },
                },
            };
        case TicketType.Outright:
            return {
                ...ticket,
                leagueName: ticket.leagueName.map(translation => ({
                    ...translation,
                })),
                teamName: ticket.teamName.map(translation => ({
                    ...translation,
                })),
            };
        default:
            return assertNever(ticket);
    }
}

export function assertNever(value: never): never {
    throw new Error(`Unhandled Ticket: ${JSON.stringify(value)}`);
}

function createBaseTicket(betPage: BetPageType): ITicket {
    return {
        key: '',
        uid: '',
        minBet: 0,
        maxBet: 0,
        stake: 0,
        betPage,
        hasMixParlay: false,
        stakeError: null,
        openTicketError: null,
        oddsChangeStatus: PriceChangeStatus.Remain,
        isOddsChangeAccepted: true,
        isPendingRemoval: false,
        isOpeningTicket: false,
        isPlacingBet: false,
    };
}

function copyMatch(match: IMatch): IMatch {
    return {
        type: 'match',
        eventId: match.eventId,
        eventCode: match.eventCode,
        hasStatistics: match.hasStatistics,
        realKickoffTime: new Date(match.realKickoffTime),
        liveCourtMatchId: match.liveCourtMatchId,
        showTime: match.showTime,
        showTimeType: match.showTimeType,
        marketTypeCount: [...match.marketTypeCount],
        isLive: match.isLive,
        mainMarketEventResult: {
            extraInfo: match.mainMarketEventResult.extraInfo === null
                ? null
                : {
                    awayRedCards: match.mainMarketEventResult.extraInfo.awayRedCards,
                    homeRedCards: match.mainMarketEventResult.extraInfo.homeRedCards,
                    injuryTime: match.mainMarketEventResult.extraInfo.injuryTime,
                    period: match.mainMarketEventResult.extraInfo.period,
                    periodStartTime: match.mainMarketEventResult.extraInfo.periodStartTime,
                    round: match.mainMarketEventResult.extraInfo.round,
                    statisticsUrl: match.mainMarketEventResult.extraInfo.statisticsUrl,
                },
            isMixParlay: match.mainMarketEventResult.isMixParlay,
            liveAwayScore: match.mainMarketEventResult.liveAwayScore,
            liveHomeScore: match.mainMarketEventResult.liveHomeScore,
        },
        eventResults: match.eventResults.map(eventResult => ({ ...eventResult })),
        homeTeam: {
            id: match.homeTeam.id,
            countryName: match.homeTeam.countryName,
            teamName: copyTranslations(match.homeTeam.teamName),
        },
        awayTeam: {
            id: match.awayTeam.id,
            countryName: match.awayTeam.countryName,
            teamName: copyTranslations(match.awayTeam.teamName),
        },
        tournament: {
            id: match.tournament.id,
            isOlympicSpecialTournament: match.tournament.isOlympicSpecialTournament,
            isSpecialTournament: match.tournament.isSpecialTournament,
            isTestOnly: match.tournament.isTestOnly,
            sport: match.tournament.sport === undefined
                ? undefined
                : {
                    id: match.tournament.sport.id,
                },
            tournamentCode: match.tournament.tournamentCode,
            tournamentInfo: match.tournament.tournamentInfo === undefined
                ? undefined
                : copyTranslations(match.tournament.tournamentInfo),
            tournamentName: match.tournament.tournamentName === undefined
                ? undefined
                : copyTranslations(match.tournament.tournamentName),
            tournamentNameEn: match.tournament.tournamentNameEn,
            tournamentSize: match.tournament.tournamentSize,
            eventsCount: match.tournament.eventsCount,
        },
        stream: match.stream === null || match.stream === undefined
            ? match.stream
            : {
                videoUrl: match.stream.videoUrl,
                betRadar: match.stream.betRadar === null || match.stream.betRadar === undefined
                    ? match.stream.betRadar
                    : {
                        eventId: match.stream.betRadar.eventId,
                        streamId: match.stream.betRadar.streamId,
                        streamStatus: match.stream.betRadar.streamStatus,
                        allowedCountries: [...match.stream.betRadar.allowedCountries],
                    },
                gLive: match.stream.gLive === null || match.stream.gLive === undefined
                    ? match.stream.gLive
                    : {
                        eventId: match.stream.gLive.eventId,
                        isPlaying: match.stream.gLive.isPlaying,
                    },
                betConstruct: match.stream.betConstruct === null || match.stream.betConstruct === undefined
                    ? match.stream.betConstruct
                    : {
                        eventId: match.stream.betConstruct.eventId,
                        allowedCountries: [...match.stream.betConstruct.allowedCountries],
                        isPlaying: match.stream.betConstruct.isPlaying,
                    },
                betGenius: match.stream.betGenius === null || match.stream.betGenius === undefined
                    ? match.stream.betGenius
                    : {
                        eventId: match.stream.betGenius.eventId,
                        allowedCountries: [...match.stream.betGenius.allowedCountries],
                        isPlaying: match.stream.betGenius.isPlaying,
                        providerEventId: match.stream.betGenius.providerEventId,
                    },
            },
        isSimulation: match.isSimulation,
        eventDate: match.eventDate,
        isMixParlay: match.isMixParlay,
        leagueId: match.leagueId,
        leagueName: copyTranslations(match.leagueName),
        leagueInformation: copyTranslations(match.leagueInformation),
        leagueSize: match.leagueSize,
        marketPage: match.marketPage,
        isTvLive: match.isTvLive,
        isSboLive: match.isSboLive,
        isHomeFavorite: match.isHomeFavorite,
        isAwayFavorite: match.isAwayFavorite,
        sportType: match.sportType,
        statisticsUrl: match.statisticsUrl,
        isToday: match.isToday,
        isClosed: match.isClosed,
        matchOrder: match.matchOrder,
    };
}

function copyOdds(odds: IOdds): IOdds {
    return {
        point: odds.point,
        betCondition: odds.betCondition,
        sortOrder: odds.sortOrder,
        isLive: odds.isLive,
        eventResult: {
            eventId: odds.eventResult.eventId,
            liveAwayScore: odds.eventResult.liveAwayScore,
            liveHomeScore: odds.eventResult.liveHomeScore,
        },
        marketType: odds.marketType,
        marketDisplayTypeName: odds.marketDisplayTypeName,
        marketGroup: {
            id: odds.marketGroup.id,
            name: odds.marketGroup.name === undefined
                ? undefined
                : copyTranslations(odds.marketGroup.name),
            homeTemplate: odds.marketGroup.homeTemplate,
            awayTemplate: odds.marketGroup.awayTemplate,
            homeExtension: odds.marketGroup.homeExtension === undefined
                ? undefined
                : copyTranslations(odds.marketGroup.homeExtension),
            awayExtension: odds.marketGroup.awayExtension === undefined
                ? undefined
                : copyTranslations(odds.marketGroup.awayExtension),
            displayPriority: odds.marketGroup.displayPriority,
        },
        oddsId: odds.oddsId,
        status: odds.status,
        eventId: odds.eventId,
        hasMixParlay: odds.hasMixParlay,
        priceOption: odds.priceOption.map(priceOption => ({
            option: priceOption.option,
            oddsOption: priceOption.oddsOption,
            price: priceOption.price,
            optionName: priceOption.optionName,
            optionNameShort: priceOption.optionNameShort,
        })),
    };
}

function copyTranslations(translations: ITranslation[] | undefined): ITranslation[] {
    return translations?.map(translation => ({
        language: translation.language,
        value: translation.value,
    })) ?? [];
}
